import React, { useMemo } from "react";
import {
  useInfiniteInvoices,
  useInfiniteMyInvoices,
  useInvoiceDetail,
} from "@src/queries/invoiceQueries";
import { theme } from "@src/theme";
import { IInvoice } from "@src/types/invoice";
import { useAuthState, useAuthCurator } from "@src/hooks/useAuth";
import { useNotification } from "@src/context/NotificationContext";
import { Loader2 } from "lucide-react";
import TableComponent from "@src/components/TableComponent";
import TableActionButton from "@src/components/TableComponent/TableActionButton";
import { UIStatusBadge } from "@src/components/StatusBadge";
import styled from "styled-components";
import DownloadIcon from "@assets/img/download-icon.svg";
import EditIcon from "@assets/img/edit-icon.svg";
import UIModal from "@src/components/UIModal/UIModal";
import EditInvoiceModal from "./EditInvoiceModal";
import Spinner from "@src/components/Spinner";
import Tooltip from "@src/components/Tooltip";

const CenteredContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50vh;
  width: 100%;
  padding: ${theme.margins["2xl"]};
`;

const PulsingLoader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  position: relative;
  margin: 0 auto;

  &::before {
    content: "";
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background-color: ${theme.colors.opacity_yellow};
    animation: loader-pulse 2s ease-in-out infinite;
  }

  @keyframes loader-pulse {
    0% {
      transform: scale(0.6);
      opacity: 0.8;
    }
    50% {
      transform: scale(1.2);
      opacity: 0.2;
    }
    100% {
      transform: scale(0.6);
      opacity: 0.8;
    }
  }
`;

const StyledLoaderIcon = styled(Loader2)`
  color: ${theme.colors.yellow};
  z-index: 1;
  filter: drop-shadow(0 0 4px ${theme.colors.opacity_yellow});
  animation: loader-spin 2s linear infinite;

  @keyframes loader-spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

interface InvoiceTableProps {
  searchText?: string;
}

const InvoiceActionCell = ({
  invoice,
  isAdminOrManager,
  onEdit,
}: {
  invoice: IInvoice;
  isAdminOrManager: boolean;
  onEdit: (invoice: IInvoice) => void;
}) => {
  const { addNotification } = useNotification();
  const [currentInvoice, setCurrentInvoice] = React.useState(invoice);

  // Safety check for production
  if (!invoice?.documentId) return null;

  const { data: pollData } = useInvoiceDetail(invoice.documentId, {
    enabled:
      currentInvoice.invoiceStatus === "submitted" && !currentInvoice.pdfUrl,
    refetchInterval: (query: any) => {
      const data = query.state.data;
      if (data?.data?.data?.pdfUrl) return false;
      return 5000;
    },
  });

  React.useEffect(() => {
    if (pollData?.data?.data) {
      setCurrentInvoice(pollData.data.data);
    }
  }, [pollData]);

  const handleExportPDF = async (inv: IInvoice): Promise<void> => {
    if (inv.pdfUrl) {
      window.open(inv.pdfUrl, "_blank");
    } else {
      addNotification(
        "PDF is still being generated. Please wait a few seconds and try again.",
        "warning"
      );
    }
  };

  return (
    <div
      style={{
        gap: 10,
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
      }}
    >
      {isAdminOrManager && currentInvoice.invoiceStatus !== "paid" && (
        <TableActionButton
          alt="edit"
          onClick={() => onEdit(currentInvoice)}
          iconSrc={EditIcon}
        />
      )}
      {currentInvoice.invoiceStatus === "submitted" &&
      !currentInvoice.pdfUrl ? (
        <div
          style={{
            width: "28px",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Tooltip content="Generating PDF..." position="top">
            <PulsingLoader>
              <StyledLoaderIcon size={16} />
            </PulsingLoader>
          </Tooltip>
        </div>
      ) : (
        <TableActionButton
          alt="export"
          onClick={() => void handleExportPDF(currentInvoice)}
          iconSrc={DownloadIcon}
        />
      )}
    </div>
  );
};

const InvoiceTable = ({ searchText }: InvoiceTableProps): React.JSX.Element => {
  const { role } = useAuthState();
  const curator = useAuthCurator();
  const isAdminOrManager = role?.type === "admin" || role?.type === "manager";
  const [editingInvoice, setEditingInvoice] = React.useState<IInvoice | null>(
    null
  );

  const allInvoicesQuery = useInfiniteInvoices({ enabled: isAdminOrManager });
  const myInvoicesQuery = useInfiniteMyInvoices(curator?.documentId, {
    enabled: !isAdminOrManager && !!curator?.documentId,
  });

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    isAdminOrManager ? allInvoicesQuery : myInvoicesQuery;

  const invoices = React.useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page?.data?.data || []);
  }, [data]);

  const tableEndRef = React.useRef<HTMLTableRowElement | null>(null);
  const isFetchingRef = React.useRef(false);
  const observerRef = React.useRef<IntersectionObserver | null>(null);
  const lastFetchTimeRef = React.useRef<number>(0);

  React.useEffect(() => {
    isFetchingRef.current = false;
    lastFetchTimeRef.current = 0;
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (!tableEndRef.current || isLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        const now = Date.now();
        const timeSinceLastFetch = now - lastFetchTimeRef.current;
        if (
          target.isIntersecting &&
          hasNextPage &&
          !isFetchingNextPage &&
          !isFetchingRef.current &&
          !isLoading &&
          timeSinceLastFetch > 1000
        ) {
          isFetchingRef.current = true;
          lastFetchTimeRef.current = now;
          if (observerRef.current) observerRef.current.disconnect();

          fetchNextPage().finally(() => {
            isFetchingRef.current = false;
            if (
              hasNextPage &&
              tableEndRef.current &&
              !isLoading &&
              !isFetchingNextPage
            ) {
              setTimeout(() => {
                if (
                  observerRef.current &&
                  tableEndRef.current &&
                  hasNextPage &&
                  !isFetchingNextPage &&
                  !isLoading &&
                  !isFetchingRef.current
                ) {
                  observerRef.current.observe(tableEndRef.current);
                }
              }, 1000);
            }
          });
        }
      },
      { root: null, rootMargin: "200px", threshold: 0.1 }
    );
    observerRef.current = observer;
    if (!isFetchingNextPage && tableEndRef.current)
      observer.observe(tableEndRef.current);
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      isFetchingRef.current = false;
      lastFetchTimeRef.current = 0;
    };
  }, [fetchNextPage, hasNextPage, isLoading, isFetchingNextPage]);

  const filteredInvoices = useMemo((): IInvoice[] => {
    if (!invoices) return [];
    if (!searchText) return invoices;
    const lowerSearch = searchText.toLowerCase();
    return (invoices as IInvoice[]).filter(
      (inv: IInvoice) =>
        (inv.billingSnapshot?.legalName || "")
          .toLowerCase()
          .includes(lowerSearch) ||
        inv.invoiceNumber.toLowerCase().includes(lowerSearch)
    );
  }, [invoices, searchText]);

  const columns = useMemo(
    () => [
      {
        Header: "#",
        accessor: "display_row_index",
        width: "40px",
      },
      {
        Header: "Invoice No.",
        accessor: "invoiceNumber",
      },
      {
        Header: "Date",
        accessor: "timestamp",
        Cell: ({ value }: { value: string }) =>
          new Date(value).toLocaleDateString(),
      },
      {
        Header: "Curator",
        accessor: "billingSnapshot",
        Cell: ({ value }: { value: any }) => value?.legalName || "-",
      },
      {
        Header: "Amount",
        accessor: "totalPrice",
        Cell: ({ value }: { value: number }) => `$${value.toFixed(2)}`,
      },
      {
        Header: "Status",
        accessor: "invoiceStatus",
        Cell: ({ value }: { value: string }) => {
          const displayStatus = value
            ? value.charAt(0).toUpperCase() + value.slice(1)
            : "Pending";
          let status: "primary" | "secondary" | "success" | "error" =
            "secondary";
          switch (value) {
            case "approved":
              status = "primary";
              break;
            case "paid":
              status = "success";
              break;
            case "cancelled":
              status = "error";
              break;
            case "submitted":
              status = "primary";
              break;
            default:
              status = "secondary";
              break;
          }
          return <UIStatusBadge text={displayStatus} $status={status} />;
        },
      },
      {
        Header: "Actions",
        accessor: "actions",
        Cell: ({ row }: { row: IInvoice }) => (
          <InvoiceActionCell
            invoice={row}
            isAdminOrManager={isAdminOrManager}
            onEdit={setEditingInvoice}
          />
        ),
      },
    ],
    [isAdminOrManager]
  );

  if (isLoading) {
    return (
      <CenteredContainer>
        <Spinner small />
      </CenteredContainer>
    );
  }

  return (
    <div style={{ width: "100%", display: "flex", flexDirection: "column" }}>
      <TableComponent
        columns={columns}
        data={filteredInvoices}
        loaderRef={tableEndRef}
      />
      {isFetchingNextPage && (
        <CenteredContainer
          style={{ minHeight: "auto", padding: theme.margins.medium }}
        >
          <Loader2 className="animate-spin" color={theme.colors.yellow} />
        </CenteredContainer>
      )}
      {editingInvoice && (
        <UIModal
          titleStyles={{ textAlign: "center" }}
          isOpen={!!editingInvoice}
          onClose={() => setEditingInvoice(null)}
          title={`Edit Invoice ${editingInvoice.invoiceNumber}`}
          actions={null}
        >
          <EditInvoiceModal
            invoice={editingInvoice}
            onSuccess={() => setEditingInvoice(null)}
            onCancel={() => setEditingInvoice(null)}
          />
        </UIModal>
      )}
    </div>
  );
};

export default InvoiceTable;
